import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatDate } from "../lib/format";
import { useAlert } from "../components/Confirm";

export function Approvers() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const list = useQuery({ queryKey: ["approvers"], queryFn: api.approvers });

  const [editing, setEditing] = useState<Record<number, any>>({});
  const [myOoo, setMyOoo] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const alertModal = useAlert();

  const approvers: any[] = list.data?.approvers ?? [];
  const today = list.data?.today ?? new Date().toISOString().slice(0, 10);
  const myRow = approvers.find((a) => a.email?.toLowerCase() === me.data?.email?.toLowerCase());
  const isAdmin = !!me.data?.roles?.admin;

  async function save(row: any) {
    const draft = editing[row.id] ?? {};
    await api.approverUpdate(row.id, {
      email: draft.email ?? row.email,
      display_name: draft.display_name ?? row.display_name,
      role: draft.role ?? row.role,
      active: draft.active ?? row.active,
      ooo_from: draft.ooo_from ?? row.ooo_from,
      ooo_to: draft.ooo_to ?? row.ooo_to,
      notes: draft.notes ?? row.notes,
    });
    setEditing((s) => ({ ...s, [row.id]: undefined as any }));
    await qc.invalidateQueries({ queryKey: ["approvers"] });
  }

  async function saveMyOoo() {
    if (myOoo.from && myOoo.to && myOoo.from > myOoo.to) {
      await alertModal({ title: "Date range invalid", body: "The end date must be on or after the start date.", confirmLabel: "OK" });
      return;
    }
    await api.meOoo(myOoo.from || null, myOoo.to || null);
    setMyOoo({ from: "", to: "" });
    await qc.invalidateQueries({ queryKey: ["approvers"] });
  }
  async function clearMyOoo() {
    await api.meOoo(null, null);
    await qc.invalidateQueries({ queryKey: ["approvers"] });
  }

  if (list.isLoading) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">Approvers</h1>
        <p className="text-slate-500 mt-1">
          Submission emails route to the active <b>primary</b> approver. If they have an OOO window covering today, routing flips to the <b>secondary</b> automatically. Both retain the ability to approve regardless (the Entra group controls who <i>can</i> approve).
        </p>
      </div>

      {/* Self-service OOO for the active approver */}
      {myRow && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-sm font-semibold text-liaise-navy">My out-of-office</div>
              <div className="text-xs text-slate-500">
                You are the <b>{myRow.role}</b> approver. Set a date range when you'll be unavailable and submissions will route to the {myRow.role === "primary" ? "secondary" : "primary"}.
              </div>
            </div>
            {myRow.on_leave_today && (
              <span className="text-xs px-2 py-1 rounded-full bg-liaise-orange/20 text-orange-800">Currently routed away</span>
            )}
          </div>
          {myRow.ooo_from && (
            <div className="text-sm text-slate-700">
              Current OOO: {formatDate(myRow.ooo_from)} → {formatDate(myRow.ooo_to)}
              <button onClick={clearMyOoo} className="ml-3 text-xs text-red-600 hover:underline">Clear</button>
            </div>
          )}
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">From</div>
              <input type="date" value={myOoo.from} onChange={(e) => setMyOoo((s) => ({ ...s, from: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
            </label>
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">To</div>
              <input type="date" value={myOoo.to} onChange={(e) => setMyOoo((s) => ({ ...s, to: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded text-sm" />
            </label>
            <button onClick={saveMyOoo} className="px-3 py-2 bg-liaise-navy text-white rounded text-sm font-medium">Set OOO</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">OOO</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">{isAdmin ? "Edit" : ""}</th>
            </tr>
          </thead>
          <tbody>
            {approvers.map((row) => {
              const draft = editing[row.id];
              const isEditing = !!draft;
              return (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 capitalize font-medium text-liaise-navy">{row.role}</td>
                  <td className="px-4 py-2">
                    {isEditing ? (
                      <input value={draft.display_name ?? row.display_name}
                        onChange={(e) => setEditing((s) => ({ ...s, [row.id]: { ...draft, display_name: e.target.value } }))}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm" />
                    ) : row.display_name}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {isEditing ? (
                      <input value={draft.email ?? row.email}
                        onChange={(e) => setEditing((s) => ({ ...s, [row.id]: { ...draft, email: e.target.value } }))}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm" />
                    ) : row.email}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {isEditing ? (
                      <div className="flex gap-2">
                        <input type="date" value={draft.ooo_from ?? row.ooo_from?.slice(0,10) ?? ""}
                          onChange={(e) => setEditing((s) => ({ ...s, [row.id]: { ...draft, ooo_from: e.target.value || null } }))}
                          className="px-2 py-1 border border-slate-200 rounded text-xs" />
                        <input type="date" value={draft.ooo_to ?? row.ooo_to?.slice(0,10) ?? ""}
                          onChange={(e) => setEditing((s) => ({ ...s, [row.id]: { ...draft, ooo_to: e.target.value || null } }))}
                          className="px-2 py-1 border border-slate-200 rounded text-xs" />
                      </div>
                    ) : row.ooo_from ? (
                      <>{formatDate(row.ooo_from)} → {formatDate(row.ooo_to)}</>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    {row.on_leave_today
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-liaise-orange/20 text-orange-800">On leave today</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-liaise-green/15 text-emerald-800">Active</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isAdmin && !isEditing && (
                      <button onClick={() => setEditing((s) => ({ ...s, [row.id]: {} }))} className="text-xs text-liaise-blue hover:underline">Edit</button>
                    )}
                    {isAdmin && isEditing && (
                      <>
                        <button onClick={() => save(row)} className="text-xs px-2 py-1 bg-liaise-navy text-white rounded">Save</button>
                        <button onClick={() => setEditing((s) => ({ ...s, [row.id]: undefined as any }))} className="text-xs px-2 py-1 ml-1 hover:underline">Cancel</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500">
        Today is {formatDate(today)}. Routing recalculates on every new submission.
      </div>
    </div>
  );
}
